/**
 * BundleBuilderModal
 * ------------------
 * Customer-facing multi-step meal deal / bundle builder.
 *
 * Responsibilities
 *  - Render each `bundle_config.groups` entry, expanding
 *    `repeat_per_person=true` groups into N sections (Person 1, Person 2 …).
 *  - After a product is picked inside a group, inline-render any reusable
 *    modifier groups referenced by that option (nested customisation:
 *    sauces, salad, extras).
 *  - Compute a live total using `bundlePricing.js` and validate the whole
 *    selection before letting the user add to basket.
 *  - Emit a single cart item with `item_type: 'bundle'` and a
 *    `bundle_selections` array that the backend uses as the source of
 *    truth (the backend re-prices it — see `price_bundle_line`).
 *
 * The modal is also reused inside the Admin "Preview Builder" drawer.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Check, ChevronDown, Copy, Minus, Plus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { menuAPI, bundleAPI } from '../../utils/api';
import * as meta from '../../analytics/meta';
import {
  computeBundleUnitPrice,
  optionUnitSurcharge,
  validateBundleSelections,
} from '../../utils/bundlePricing';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const priceLabel = (n) => `£${(Math.round(n * 100) / 100).toFixed(2)}`;

const expandGroupSlots = (grp) => {
  if (!grp) return [];
  if (!grp.repeat_per_person) {
    return [{ key: '__solo__', label: null }];
  }
  const count = Math.max(1, Number(grp.person_count || 1));
  const labels = grp.person_labels || [];
  return Array.from({ length: count }, (_, i) => {
    const label = labels[i] || `Person ${i + 1}`;
    return { key: label, label };
  });
};

const resolveOptionSourceIds = (grp) => {
  const src = grp?.source || {};
  if (src.kind === 'menu_items') return src.menu_item_ids || [];
  if (src.kind === 'categories') return [];
  return [];
};

const buildOptionCatalog = (grp, sourceProducts) => {
  // Map every option in the group to a { display, unitSurcharge } dict.
  // For source kinds "menu_items" / "categories" the display name comes
  // from the referenced menu item. For "custom" and "modifier_group" the
  // option's display_name_override or custom_options entry is used.
  const src = grp?.source || {};
  const options = grp.options || [];
  return options.map((opt) => {
    let sourceDoc = null;
    if (opt.source_ref) sourceDoc = sourceProducts[opt.source_ref] || null;
    let displayName = opt.display_name_override;
    if (!displayName && sourceDoc) displayName = sourceDoc.name;
    if (!displayName && src.kind === 'custom') {
      const cust = (src.custom_options || []).find((c) => c.id === opt.source_ref);
      displayName = cust?.name;
    }
    if (!displayName) displayName = 'Option';
    return {
      option: opt,
      displayName,
      surcharge: optionUnitSurcharge(opt, sourceDoc),
      sourceDoc,
      available: sourceDoc ? sourceDoc.available !== false : opt.active !== false,
    };
  });
};

// ---------------------------------------------------------------------------
// Nested modifier group renderer
// ---------------------------------------------------------------------------
const NestedModifierGroup = ({ group, value, onChange }) => {
  if (!group) return null;
  const isRadio = group.type === 'radio';
  const current = value || (isRadio ? null : []);
  const toggleCheckbox = (choice, checked) => {
    if (isRadio) {
      onChange({ name: choice.name, price: Number(choice.price || 0) });
      return;
    }
    const arr = Array.isArray(current) ? [...current] : [];
    const idx = arr.findIndex((c) => c.name === choice.name);
    if (checked && idx === -1) arr.push({ name: choice.name, price: Number(choice.price || 0) });
    if (!checked && idx !== -1) arr.splice(idx, 1);
    onChange(arr);
  };
  return (
    <div className="mt-3 pl-4 border-l-2 border-primary/20 space-y-1.5" data-testid="nested-modifier-group">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {group.title}
        </p>
        {group.required && (
          <span className="text-[10px] text-orange-600">Required</span>
        )}
      </div>
      {isRadio ? (
        <RadioGroup
          value={current?.name || ''}
          onValueChange={(name) => {
            const c = (group.choices || []).find((x) => x.name === name);
            if (c) onChange({ name: c.name, price: Number(c.price || 0) });
          }}
          className="grid grid-cols-2 gap-1.5"
        >
          {(group.choices || []).filter((c) => c.active !== false).map((c) => (
            <label
              key={c.name}
              className={`flex items-center justify-between text-xs p-2 rounded-md cursor-pointer border ${
                current?.name === c.name ? 'border-primary bg-primary/5' : 'border-gray-200'
              }`}
            >
              <span className="flex items-center gap-2">
                <RadioGroupItem value={c.name} className="h-3.5 w-3.5" />
                {c.name}
              </span>
              {Number(c.price) > 0 && (
                <span className="text-xs font-medium">+{priceLabel(Number(c.price))}</span>
              )}
            </label>
          ))}
        </RadioGroup>
      ) : (
        <div className="grid grid-cols-2 gap-1.5">
          {(group.choices || []).filter((c) => c.active !== false).map((c) => {
            const isOn = (current || []).some((x) => x.name === c.name);
            return (
              <label
                key={c.name}
                className={`flex items-center justify-between text-xs p-2 rounded-md cursor-pointer border ${
                  isOn ? 'border-primary bg-primary/5' : 'border-gray-200'
                }`}
              >
                <span className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isOn}
                    onChange={(e) => toggleCheckbox(c, e.target.checked)}
                    className="h-3.5 w-3.5 accent-primary"
                  />
                  {c.name}
                </span>
                {Number(c.price) > 0 && (
                  <span className="text-xs font-medium">+{priceLabel(Number(c.price))}</span>
                )}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Group / slot renderer
// ---------------------------------------------------------------------------
const GroupSlot = ({
  group,
  slot,
  slotIndex,
  catalog,
  currentPicks,
  onPickChange,
  onNestedChange,
  modifierGroupsById,
}) => {
  const isRadio = group.selection_type !== 'multiple' && (group.max_selections || 1) === 1;
  const pickForSlot = currentPicks.filter((p) => (p.person_label || null) === (slot.label || null));
  const singlePick = pickForSlot[0] || null;

  const headingBase = group.title || 'Please select';
  const heading = group.repeat_per_person && slot.label
    ? `${slot.label}: ${headingBase.replace(/^person\s+\d+\s*:\s*/i, '')}`
    : headingBase;

  return (
    <div className="border-t pt-4" data-testid={`bundle-group-${group.id}-slot-${slotIndex}`}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-semibold text-base">{heading}</h4>
        <span
          className={`text-[11px] px-2 py-0.5 rounded-full ${
            group.required
              ? pickForSlot.length > 0
                ? 'bg-green-100 text-green-700'
                : 'bg-orange-100 text-orange-700'
              : 'text-muted-foreground'
          }`}
        >
          {group.required ? (pickForSlot.length > 0 ? 'Done' : 'Required') : 'Optional'}
        </span>
      </div>
      {group.instructions && (
        <p className="text-xs text-muted-foreground mb-2">{group.instructions}</p>
      )}
      <div className="space-y-2">
        {catalog.map(({ option, displayName, surcharge, available }) => {
          const chosen = isRadio
            ? singlePick?.option_id === option.id
            : pickForSlot.some((p) => p.option_id === option.id);
          const disabled = !available || option.active === false;
          return (
            <div
              key={option.id}
              role="button"
              tabIndex={0}
              onClick={() => !disabled && onPickChange(group, slot, option, !chosen)}
              onKeyDown={(e) => {
                if (disabled) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onPickChange(group, slot, option, !chosen);
                }
              }}
              className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                disabled
                  ? 'opacity-40 cursor-not-allowed'
                  : chosen
                    ? 'bg-primary/10 border-primary/30 cursor-pointer'
                    : 'hover:bg-secondary/40 border-gray-200 cursor-pointer'
              }`}
              data-testid="bundle-option"
            >
              <div className="flex items-center gap-3">
                <div
                  className={`h-4 w-4 rounded-full border flex items-center justify-center ${
                    chosen ? 'bg-primary border-primary' : 'border-gray-400'
                  }`}
                >
                  {chosen && <Check className="h-3 w-3 text-white" />}
                </div>
                <Label className="cursor-pointer font-medium">
                  {displayName}
                  {disabled && !available && (
                    <span className="ml-2 text-xs text-red-500">Sold out</span>
                  )}
                </Label>
              </div>
              {surcharge > 0 ? (
                <span className="text-sm font-medium">+{priceLabel(surcharge)}</span>
              ) : surcharge < 0 ? (
                <span className="text-sm font-medium text-green-600">
                  {priceLabel(surcharge)}
                </span>
              ) : (
                <span className="text-xs text-muted-foreground">Included</span>
              )}
            </div>
          );
        })}
      </div>
      {/* Nested modifier groups appear once a product is picked */}
      {group.repeat_per_person && singlePick && (
        <div className="mt-2">
          {(catalog.find((c) => c.option.id === singlePick.option_id)?.option
            ?.nested_modifier_group_ids || [])
            .map((mgid) => modifierGroupsById[mgid])
            .filter(Boolean)
            .map((mg) => (
              <NestedModifierGroup
                key={mg.id}
                group={mg}
                value={singlePick.nested_customizations?.[mg.id]}
                onChange={(val) => onNestedChange(group, slot, singlePick, mg.id, val)}
              />
            ))}
        </div>
      )}
      {/* For non-repeat groups nested modifiers also apply — attach to each pick */}
      {!group.repeat_per_person &&
        pickForSlot.map((pick) => {
          const opt = catalog.find((c) => c.option.id === pick.option_id)?.option;
          const nestedIds = opt?.nested_modifier_group_ids || [];
          if (!nestedIds.length) return null;
          return (
            <div key={pick.option_id} className="mt-2">
              {nestedIds.map((mgid) => modifierGroupsById[mgid]).filter(Boolean).map((mg) => (
                <NestedModifierGroup
                  key={mg.id}
                  group={mg}
                  value={pick.nested_customizations?.[mg.id]}
                  onChange={(val) => onNestedChange(group, slot, pick, mg.id, val)}
                />
              ))}
            </div>
          );
        })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main modal
// ---------------------------------------------------------------------------
export const BundleBuilderModal = ({ bundle, open, initialSelections = null, initialQuantity = 1, onClose, onAdd }) => {
  const [selections, setSelections] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [attempted, setAttempted] = useState(false);
  const [sourceProducts, setSourceProducts] = useState({}); // id → product
  const [modifierGroupsById, setModifierGroupsById] = useState({});
  const [loading, setLoading] = useState(true);

  // Reset state each time modal opens
  useEffect(() => {
    if (!open) return;
    setSelections(initialSelections ? [...initialSelections] : []);
    setQuantity(Math.max(1, Number(initialQuantity) || 1));
    setAttempted(false);
  }, [open, bundle?.id, initialSelections, initialQuantity]);

  // Fetch referenced products + modifier groups the first time we render
  useEffect(() => {
    if (!open || !bundle) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [menuRes, groupsRes] = await Promise.all([
          menuAPI.getMenu(),
          bundleAPI.listModifierGroups(false),
        ]);
        if (cancelled) return;
        const byId = {};
        (menuRes.data || []).forEach((m) => (byId[m.id] = m));
        // Include products in the referenced categories
        const productsCache = { ...byId };
        setSourceProducts(productsCache);
        const modById = {};
        (groupsRes.data || []).forEach((g) => (modById[g.id] = g));
        setModifierGroupsById(modById);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, bundle?.id]);

  // Compute the catalog for each group, factoring in category-sourced groups
  const groups = useMemo(() => {
    const cfg = bundle?.bundle_config?.groups || [];
    return [...cfg].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  }, [bundle]);

  const groupCatalogs = useMemo(() => {
    const map = {};
    groups.forEach((grp) => {
      let options = grp.options || [];
      // If sourced from categories, auto-populate options from menu products
      // in those categories that are currently active.
      const src = grp.source || {};
      if (src.kind === 'categories') {
        const catSet = new Set((src.category_names || []).map((c) => c.toLowerCase()));
        // Only include products with an explicit option config; if none, we
        // synthesize "included" options for every product in the category.
        if (!options.length) {
          options = Object.values(sourceProducts)
            .filter((p) => catSet.has((p.category || '').toLowerCase()) && p.available !== false)
            .map((p) => ({
              id: `auto-${p.id}`,
              source_ref: p.id,
              pricing_mode: 'included',
              active: true,
              nested_modifier_group_ids: [],
            }));
        }
      }
      const dummyGroup = { ...grp, options };
      map[grp.id] = { group: dummyGroup, catalog: buildOptionCatalog(dummyGroup, sourceProducts) };
    });
    return map;
  }, [groups, sourceProducts]);

  const handlePickChange = (grp, slot, option, checked) => {
    setSelections((prev) => {
      const isRadio = grp.selection_type !== 'multiple' && (grp.max_selections || 1) === 1;
      let next = [...prev];
      const slotLabel = slot.label || null;

      if (isRadio) {
        // Replace this slot's single selection
        next = next.filter(
          (s) => !(s.group_id === grp.id && (s.person_label || null) === slotLabel),
        );
        if (checked) {
          next.push({
            group_id: grp.id,
            group_title: grp.title,
            person_label: slotLabel,
            option_id: option.id,
            nested_customizations: {},
          });
        }
      } else {
        if (checked) {
          next.push({
            group_id: grp.id,
            group_title: grp.title,
            person_label: slotLabel,
            option_id: option.id,
            nested_customizations: {},
          });
        } else {
          next = next.filter(
            (s) =>
              !(
                s.group_id === grp.id &&
                (s.person_label || null) === slotLabel &&
                s.option_id === option.id
              ),
          );
        }
      }
      return next;
    });
  };

  const handleNestedChange = (grp, slot, pick, mgId, value) => {
    setSelections((prev) =>
      prev.map((s) => {
        if (
          s.group_id === grp.id &&
          (s.person_label || null) === (slot.label || null) &&
          s.option_id === pick.option_id
        ) {
          const nc = { ...(s.nested_customizations || {}) };
          if (value == null || (Array.isArray(value) && value.length === 0)) {
            delete nc[mgId];
          } else {
            nc[mgId] = value;
          }
          return { ...s, nested_customizations: nc };
        }
        return s;
      }),
    );
  };

  const copyPersonOneToAll = () => {
    setSelections((prev) => {
      const groupSlots = {};
      groups.forEach((grp) => {
        if (!grp.repeat_per_person) return;
        groupSlots[grp.id] = expandGroupSlots(grp);
      });
      const next = prev.filter((s) => {
        const grp = groups.find((g) => g.id === s.group_id);
        if (!grp?.repeat_per_person) return true;
        // Keep only Person 1 selections; drop the rest — we'll re-emit clones.
        const slots = groupSlots[grp.id];
        const firstLabel = slots?.[0]?.label;
        return (s.person_label || null) === (firstLabel || null);
      });
      Object.entries(groupSlots).forEach(([gid, slots]) => {
        const firstLabel = slots[0]?.label;
        const personOnePicks = prev.filter(
          (s) => s.group_id === gid && (s.person_label || null) === (firstLabel || null),
        );
        slots.slice(1).forEach((slot) => {
          personOnePicks.forEach((p) => {
            next.push({ ...p, person_label: slot.label });
          });
        });
      });
      return next;
    });
  };

  const unitPrice = useMemo(
    () => computeBundleUnitPrice(bundle, selections, (id) => sourceProducts[id]),
    [bundle, selections, sourceProducts],
  );

  const validation = useMemo(
    () => validateBundleSelections(bundle, selections),
    [bundle, selections],
  );

  const missingCount = validation.missing.length + validation.errors.length;

  const handleAdd = () => {
    if (!validation.valid) {
      setAttempted(true);
      return;
    }
    // Enrich selections with display info so the cart and receipt can render
    // without re-fetching. Server will re-derive/verify these on checkout.
    const enrichedSelections = selections.map((s) => {
      const grp = groups.find((g) => g.id === s.group_id);
      const opt = (grp?.options || []).find((o) => o.id === s.option_id);
      const sourceDoc = opt?.source_ref ? sourceProducts[opt.source_ref] : null;
      const displayName =
        opt?.display_name_override ||
        sourceDoc?.name ||
        s.display_name ||
        'Selection';
      return {
        ...s,
        source_ref: opt?.source_ref || null,
        display_name: displayName,
        pricing_mode: opt?.pricing_mode || 'included',
        unit_surcharge: optionUnitSurcharge(opt, sourceDoc),
      };
    });
    onAdd({
      item_id: bundle.id,
      name: bundle.name,
      price: unitPrice,
      quantity,
      image_url: bundle.image_url,
      item_type: 'bundle',
      bundle_selections: enrichedSelections,
      fixed_items_snapshot: (bundle.bundle_config?.fixed_items || []).map((f) => ({
        name: f.custom_name,
        quantity: Number(f.quantity || 1),
        item_ref: f.item_ref || null,
      })),
      customizations: {},
    });
    // Meta Pixel `CustomizeProduct` — signal that the customer finished
    // building a bundle. AddToCart will still fire from CartContext right
    // after this, but CustomizeProduct is what Ads Manager uses to
    // optimise for engaged (customized) meals vs quick adds.
    meta.track('CustomizeProduct', {
      content_ids: [bundle.id],
      content_name: bundle.name,
      content_type: 'product',
      currency: 'GBP',
      value: unitPrice * quantity,
      num_items: enrichedSelections.length,
    });
    onClose && onClose();
  };

  if (!bundle) return null;
  const cfg = bundle.bundle_config || {};
  const hasRepeatGroup = groups.some((g) => g.repeat_per_person);
  // Only offer "Copy Person 1" when at least one repeat group allows the same
  // option across people — otherwise clicking it would deterministically make
  // the basket unshippable.
  const canCopyPersonOne = groups.some(
    (g) => g.repeat_per_person && (g.allow_duplicates || g.selection_type === 'multiple'),
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-2xl max-h-[92vh] overflow-hidden flex flex-col p-0"
        data-testid="bundle-builder-modal"
      >
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="text-2xl font-bold">{bundle.name}</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {cfg.feeds_label || 'Build your meal deal — pick each option below.'}
          </DialogDescription>
          {cfg.feeds_label && (
            <p className="sr-only">{cfg.feeds_label}</p>
          )}
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 py-3 space-y-4">
          {bundle.description && (
            <p className="text-sm text-muted-foreground">{bundle.description}</p>
          )}
          {!!(cfg.fixed_items || []).length && (
            <div className="rounded-lg bg-secondary/40 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                Included in the meal
              </p>
              <ul className="text-sm space-y-0.5">
                {cfg.fixed_items.map((fx, i) => (
                  <li key={i}>
                    • {fx.quantity}× {fx.custom_name}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {attempted && !validation.valid && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2" data-testid="bundle-validation-banner">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">
                  Please finish your selections
                </p>
                {validation.missing.map((m) => (
                  <p key={m.groupId + m.label} className="text-sm text-red-600">
                    • {m.label}
                  </p>
                ))}
                {validation.errors.map((e, i) => (
                  <p key={i} className="text-sm text-red-600">
                    • {e}
                  </p>
                ))}
              </div>
            </div>
          )}
          {hasRepeatGroup && canCopyPersonOne && (
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={copyPersonOneToAll}
                className="rounded-full text-xs"
                data-testid="copy-person-one-btn"
              >
                <Copy className="h-3 w-3 mr-1.5" />
                Copy Person 1 to everyone
              </Button>
            </div>
          )}
          {loading ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              Loading meal options…
            </div>
          ) : (
            groups.map((grp) => {
              const slots = expandGroupSlots(grp);
              const catalog = groupCatalogs[grp.id]?.catalog || [];
              const currentPicks = selections.filter((s) => s.group_id === grp.id);
              return (
                <div key={grp.id} data-testid={`bundle-group-${grp.id}`}>
                  {slots.map((slot, idx) => (
                    <GroupSlot
                      key={slot.key + idx}
                      group={grp}
                      slot={slot}
                      slotIndex={idx}
                      catalog={catalog}
                      currentPicks={currentPicks}
                      onPickChange={handlePickChange}
                      onNestedChange={handleNestedChange}
                      modifierGroupsById={modifierGroupsById}
                    />
                  ))}
                </div>
              );
            })
          )}
        </div>
        <DialogFooter className="px-6 py-4 border-t bg-white sticky bottom-0 flex-col sm:flex-col gap-3">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="icon"
                className="h-10 w-10 rounded-full"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                data-testid="bundle-decrease-quantity"
              >
                <Minus className="h-4 w-4" />
              </Button>
              <span className="text-lg font-bold w-8 text-center">{quantity}</span>
              <Button
                variant="outline"
                size="icon"
                className="h-10 w-10 rounded-full"
                onClick={() => setQuantity(quantity + 1)}
                data-testid="bundle-increase-quantity"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Meal total</p>
              <p className="text-xl font-bold text-primary" data-testid="bundle-total">
                {priceLabel(unitPrice * quantity)}
              </p>
            </div>
          </div>
          <Button
            className={`w-full rounded-full h-12 text-base ${
              !validation.valid && attempted ? 'bg-orange-500 hover:bg-orange-600' : ''
            }`}
            onClick={handleAdd}
            data-testid="add-bundle-to-cart"
          >
            {validation.valid ? (
              <>Add Meal to Basket · {priceLabel(unitPrice * quantity)}</>
            ) : validation.missing.length > 0 ? (
              <>
                <AlertCircle className="h-4 w-4 mr-2" />
                {validation.missing.length} selection{validation.missing.length === 1 ? '' : 's'} needed
              </>
            ) : (
              <>
                <AlertCircle className="h-4 w-4 mr-2" />
                Fix your selections
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BundleBuilderModal;
